import { useState } from 'react'
import { useParticipants } from '@/entities/call'
import { ParticipantVolume } from '@/features/adjust-participant-volume'
import { AdmitGuestsPanel, useLobby } from '@/features/admit-guests'
import { ChatPanel, type ChatView, useChatMessages } from '@/features/call-chat'
import { LockMeetingControl } from '@/features/lock-meeting'
import {
  ParticipantList,
  useKickParticipant,
  useMuteParticipant,
} from '@/features/moderate-participants'
import { cn, copyText } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Icon } from '@/shared/ui/icon'
import { toast } from '@/shared/ui/toast'

type Tab = 'participants' | 'chat' | 'lobby'

interface CallSidebarProps {
  meetingId: string
  /** Модерация (mute/kick/end) — только организатор. */
  canModerate?: boolean
  /** Лобби (видеть очередь и впускать гостей) — любой сотрудник. */
  canAdmit?: boolean
  /** Ссылка для приглашения гостя; есть у любого сотрудника (не только организатора). */
  guestUrl?: string
  /** Замок звонка: закрытый отправляет входящих, включая сотрудников, в лобби. */
  locked?: boolean
  onSendChat: (text: string) => void
  onClose?: () => void
}

export function CallSidebar({
  meetingId,
  canModerate = false,
  canAdmit = false,
  guestUrl,
  locked = false,
  onSendChat,
  onClose,
}: CallSidebarProps) {
  const [tab, setTab] = useState<Tab>('participants')
  const participants = useParticipants()
  const messages = useChatMessages()
  const mute = useMuteParticipant(meetingId)
  const kick = useKickParticipant(meetingId)
  const lobby = useLobby(meetingId, canAdmit)

  const waitingCount = lobby.data?.length ?? 0

  const chatViews: ChatView[] = messages.map((message) => ({
    id: message.id,
    text: message.text,
    authorName: participants.find((p) => p.id === message.authorId)?.name ?? 'Участник',
    mine: message.authorId === 'local',
  }))

  const tabs: Array<{ id: Tab; label: string; badge?: number }> = [
    { id: 'participants', label: 'Участники', badge: participants.length },
    { id: 'chat', label: 'Чат' },
    ...(canAdmit ? [{ id: 'lobby' as const, label: 'Ожидают', badge: waitingCount }] : []),
  ]

  return (
    <div className="flex h-full min-h-0 flex-col rounded-card border border-border-default bg-bg-surface">
      <div className="flex items-center gap-1 border-border-default border-b p-2">
        <div role="tablist" aria-label="Панель звонка" className="flex min-w-0 flex-1 gap-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={cn(
                'flex items-center gap-2 rounded-control px-3 py-1.5 text-sm transition-colors',
                tab === t.id
                  ? 'bg-bg-elevated text-text-primary'
                  : 'text-text-secondary hover:text-text-primary',
              )}
            >
              {t.label}
              {t.badge != null && t.badge > 0 && (
                <span className="rounded-pill bg-bg-raised px-1.5 font-mono text-xs">
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>
        <LockMeetingControl meetingId={meetingId} locked={locked} />
        {onClose && (
          <button
            type="button"
            aria-label="Закрыть панель"
            onClick={onClose}
            className="shrink-0 rounded-control px-2 py-1.5 text-text-secondary hover:text-text-primary lg:hidden"
          >
            <Icon name="close" size={18} />
          </button>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {tab === 'participants' && (
          <div className="flex flex-col gap-3">
            {guestUrl && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  void copyText(guestUrl).then((ok) =>
                    ok
                      ? toast.success('Ссылка для гостя скопирована')
                      : toast.error('Не удалось скопировать ссылку'),
                  )
                }
              >
                <Icon name="copy" size={16} />
                Скопировать ссылку для гостя
              </Button>
            )}
            <ParticipantList
              participants={participants}
              canModerate={canModerate}
              onMute={(id) => mute.mutate(id)}
              onKick={(id) => kick.mutate(id)}
              renderControls={(p) => (
                <ParticipantVolume participantId={p.id} participantName={p.name} />
              )}
            />
          </div>
        )}
        {tab === 'chat' && <ChatPanel messages={chatViews} onSend={onSendChat} />}
        {tab === 'lobby' && canAdmit && <AdmitGuestsPanel meetingId={meetingId} />}
      </div>
    </div>
  )
}
